import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Plug, CheckCircle2, AlertCircle, Loader2, Save, Link2, Link2Off, ExternalLink,
  Database, RefreshCw, Megaphone, Webhook, Camera,
} from 'lucide-react';
import { useFacility } from '../context/FacilityContext';
import WebhooksCard from '../components/settings/WebhooksCard';
import MasConnectionCard from '../components/integrations/MasConnectionCard';
import CameraEngineConnectionCard from '../components/integrations/CameraEngineConnectionCard';
import EngineConnectionCard from '../components/integrations/EngineConnectionCard';
import { formatDateTime } from '../utils/datetime';
import integrationsService from '../services/integrationsService';

/**
 * Settings → Third-party apps. Operator connects external tools (QuickBooks
 * Online today). Credentials are entered here and stored ENCRYPTED at rest by
 * the backend; secrets are never sent back — the UI shows masked status only.
 * Operator-agnostic: every operator connects their OWN QuickBooks company.
 */

const schema = z.object({
  environment: z.enum(['sandbox', 'production']),
  realm_id: z.string().trim().optional().or(z.literal('')),
  client_id: z.string().trim().optional().or(z.literal('')),
  client_secret: z.string().optional().or(z.literal('')),
  refresh_token: z.string().optional().or(z.literal('')),
});

// QB Phase 1 read-viewer helpers. Generic so they work across QBO entities.
const APP_TABS = [
  { key: 'mas', label: 'Marketing engine', icon: Megaphone },
  { key: 'camera', label: 'Camera intelligence', icon: Camera },
  // Served by the generic connected-services wires (services/engine_registry)
  // rather than a card of its own — every future service lands here the same
  // way, with a descriptor instead of a subsystem.
  { key: 'esign', label: 'Document signing', icon: Database },
  { key: 'quickbooks', label: 'QuickBooks', icon: Database },
  { key: 'webhooks', label: 'Webhooks', icon: Webhook },
];

const READ_ENTITIES = ['Customer', 'Invoice', 'Payment', 'Account', 'Item', 'Vendor', 'Bill', 'CompanyInfo'];
const PRIORITY_COLS = ['Id', 'DisplayName', 'Name', 'FullyQualifiedName', 'CompanyName', 'DocNumber', 'TxnDate', 'DueDate', 'TotalAmt', 'Balance', 'Amount', 'CurrentBalance', 'AccountType', 'Active'];
const isScalar = (v) => ['string', 'number', 'boolean'].includes(typeof v);
const columnsFor = (rows) => {
  const present = new Set();
  (rows || []).slice(0, 50).forEach((r) => Object.entries(r || {}).forEach(([k, v]) => {
    if (isScalar(v) && v !== '') present.add(k);
  }));
  const ordered = [...PRIORITY_COLS.filter((k) => present.has(k)), ...[...present].filter((k) => !PRIORITY_COLS.includes(k))];
  return ordered.slice(0, 6);
};
const cellVal = (v) => (typeof v === 'boolean' ? (v ? 'Yes' : 'No') : v == null ? '' : String(v));

const StatusBadge = ({ qb }) => {
  if (qb?.status === 'connected') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[11px] font-medium">
        <CheckCircle2 className="h-3 w-3" /> Connected
      </span>
    );
  }
  if (qb?.configured) {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-medium">
        <AlertCircle className="h-3 w-3" /> Configured · not tested
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 text-[11px] font-medium">
      Not connected
    </span>
  );
};

const ThirdPartyAppsSection = () => {
  const { facilityId, currentFacility } = useFacility();
  const timeZone = currentFacility?.timezone || undefined;
  const [searchParams, setSearchParams] = useSearchParams();
  const appTab = searchParams.get('app') || 'mas';
  const setAppTab = (key) => {
    const p = new URLSearchParams(searchParams);
    if (key === 'mas') p.delete('app'); else p.set('app', key);
    setSearchParams(p, { replace: true });
  };
  const [qb, setQb] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [msg, setMsg] = useState(null); // {type:'ok'|'err', text}
  // QB read viewer (read-only; no import).
  const [reading, setReading] = useState(false);
  const [readEntity, setReadEntity] = useState('Customer');
  const [readResult, setReadResult] = useState(null); // {entity,count,rows,fetched_at}
  const [showRaw, setShowRaw] = useState(false);
  // The two directions are separate jobs and were previously separate
  // surfaces (a read strip here, an "Accounting sync" card far below the
  // credentials form — easy to miss entirely). Tabs put them side by side:
  //   read  = QuickBooks -> mySMS360 (preview only, nothing imported)
  //   write = mySMS360 -> QuickBooks (the real accounting sync)
  const [qbTab, setQbTab] = useState('read');

  const {
    register, handleSubmit, reset, watch,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: { environment: 'production', realm_id: '', client_id: '', client_secret: '', refresh_token: '' },
  });

  const load = useCallback(async () => {
    if (!facilityId) { setLoading(false); return; }
    setLoading(true);
    try {
      const res = await integrationsService.list(facilityId);
      const q = res.data.quickbooks;
      setQb(q);
      reset({
        environment: q?.environment || 'production',
        realm_id: q?.realm_id || '',
        client_id: '', client_secret: '', refresh_token: '',
      });
    } catch (e) {
      setMsg({ type: 'err', text: 'Unable to load integrations.' });
    } finally {
      setLoading(false);
    }
  }, [facilityId, reset]);

  useEffect(() => { load(); }, [load]);

  // Handle the return bounce from the QuickBooks OAuth callback.
  useEffect(() => {
    const qbo = searchParams.get('qbo');
    if (!qbo) return;
    setMsg(qbo === 'connected'
      ? { type: 'ok', text: 'QuickBooks connected.' }
      : { type: 'err', text: 'QuickBooks connection was cancelled or failed.' });
    const p = new URLSearchParams(searchParams);
    p.delete('qbo');
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onConnect = async () => {
    setConnecting(true);
    setMsg(null);
    try {
      const res = await integrationsService.authorizeQuickBooks(facilityId);
      window.location.href = res.data.authorize_url; // full-page redirect to Intuit
    } catch (e) {
      setConnecting(false);
      setMsg({ type: 'err', text: e?.response?.data?.detail || 'Could not start QuickBooks connect.' });
    }
  };

  const onSubmit = async (data) => {
    setSaving(true);
    setMsg(null);
    try {
      // Only send secrets the operator actually typed; blank = keep stored.
      const payload = { environment: data.environment, realm_id: data.realm_id || '' };
      if (data.client_id?.trim()) payload.client_id = data.client_id.trim();
      if (data.client_secret?.trim()) payload.client_secret = data.client_secret.trim();
      if (data.refresh_token?.trim()) payload.refresh_token = data.refresh_token.trim();
      const res = await integrationsService.saveQuickBooks(facilityId, payload);
      setQb(res.data);
      reset({
        environment: res.data?.environment || 'production',
        realm_id: res.data?.realm_id || '',
        client_id: '', client_secret: '', refresh_token: '',
      });
      setMsg({ type: 'ok', text: 'Saved. Click “Test connection” to verify.' });
    } catch (e) {
      setMsg({ type: 'err', text: e?.response?.data?.detail || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    setMsg(null);
    try {
      const res = await integrationsService.testQuickBooks(facilityId);
      if (res.data.ok) {
        setMsg({ type: 'ok', text: `Connected to “${res.data.company_name}” (${res.data.environment}).` });
        load();
      } else {
        setMsg({ type: 'err', text: res.data.error || 'Connection test failed.' });
      }
    } catch (e) {
      setMsg({ type: 'err', text: e?.response?.data?.detail || 'Connection test failed.' });
    } finally {
      setTesting(false);
    }
  };

  const onRead = async () => {
    setReading(true);
    setMsg(null);
    try {
      const res = await integrationsService.readQuickBooks(facilityId, readEntity, 50);
      setReadResult(res.data);
      setShowRaw(false);
      if (!res.data.count) setMsg({ type: 'ok', text: `No ${readEntity} records found in QuickBooks.` });
    } catch (e) {
      setReadResult(null);
      setMsg({ type: 'err', text: e?.response?.data?.detail || 'QuickBooks read failed.' });
    } finally {
      setReading(false);
    }
  };

  const onDisconnect = async () => {
    if (!window.confirm('Disconnect QuickBooks? Stored credentials will be removed.')) return;
    setMsg(null);
    try {
      await integrationsService.remove(facilityId, 'quickbooks');
      setMsg({ type: 'ok', text: 'QuickBooks disconnected.' });
      load();
    } catch (e) {
      setMsg({ type: 'err', text: e?.response?.data?.detail || 'Disconnect failed.' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-5 w-5 animate-spin text-blue-600" />
      </div>
    );
  }

  const env = watch('environment');
  const ph = (set, fallback) => (set ? '•••• stored — leave blank to keep' : fallback);

  return (
    <div className="max-w-2xl">
      {msg && (
        <div className={`mb-4 rounded-lg px-4 py-2.5 text-[13px] border ${
          msg.type === 'ok'
            ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
            : 'border-red-100 bg-red-50 text-red-600'}`}>
          {msg.text}
        </div>
      )}

      {/* One app per tab — the page grew past a single scroll once the
          marketing engine joined QuickBooks and webhooks. */}
      <div className="border-b border-slate-200 mb-4">
        <nav className="-mb-px flex gap-5 flex-wrap" aria-label="Connected apps">
          {APP_TABS.map((t) => {
            const on = appTab === t.key;
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setAppTab(t.key)}
                className={`inline-flex items-center gap-1.5 whitespace-nowrap py-2.5 px-1 border-b-2 text-[13px] font-medium transition-colors ${
                  on
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'}`}
              >
                <Icon className="h-3.5 w-3.5" /> {t.label}
              </button>
            );
          })}
        </nav>
      </div>

      {/* M.A.S connection — one key per facility, stored encrypted. The
          marketing surface itself lives under Marketing. */}
      {appTab === 'mas' && <MasConnectionCard />}

      {/* Camera intelligence engine — a microservice each facility connects
          to (own URL, site key + webhook secret, stored encrypted). The
          camera surface itself lives under AI Camera Agent. */}
      {appTab === 'camera' && <CameraEngineConnectionCard />}

      {appTab === 'esign' && (
        <EngineConnectionCard
          service="esign"
          label="electronic document signing"
          description="Send documents for signature and track them to completion"
        />
      )}

      {appTab === 'quickbooks' && (
      <div className="rounded-xl border border-slate-200/80 bg-white p-5">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Plug className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-[15px] font-semibold text-slate-900">QuickBooks Online</h3>
              <p className="text-[12px] text-slate-400">Sync payments, invoices and customers from your QBO company.</p>
            </div>
          </div>
          <StatusBadge qb={qb} />
        </div>

        {qb?.status === 'connected' && qb?.company_name && (
          <p className="mb-4 text-[12px] text-slate-500">
            Linked company: <span className="font-medium text-slate-700">{qb.company_name}</span>
            {qb.connected_at ? ` · verified ${formatDateTime(qb.connected_at, timeZone)}` : ''}
          </p>
        )}

        {/* Read / Write tabs — the two directions, one card. */}
        {(qb?.status === 'connected' || qb?.configured) && (
          <div className="mb-3 flex items-center gap-1 border-b border-slate-200">
            {[
              { key: 'read', label: 'Read', Icon: Database,
                hint: 'Preview QuickBooks records — nothing is imported' },
              { key: 'write', label: 'Write', Icon: RefreshCw,
                hint: 'Push invoices, payments and refunds into QuickBooks' },
            ].map(({ key, label, Icon, hint }) => (
              <button
                key={key} type="button" title={hint}
                onClick={() => setQbTab(key)}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors ${
                  qbTab === key
                    ? 'border-blue-600 text-blue-700'
                    : 'border-transparent text-slate-500 hover:text-slate-700'}`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
            <span className="ml-auto text-[11px] text-slate-400 pb-2 hidden sm:block">
              {qbTab === 'read'
                ? 'QuickBooks → mySMS360'
                : 'mySMS360 → QuickBooks'}
            </span>
          </div>
        )}

        {/* READ — read-only data viewer. Reads QBO entities for display;
            nothing is imported into mySMS360. */}
        {qbTab === 'read' && qb?.status === 'connected' && (
          <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
            <div className="flex items-center gap-2 flex-wrap">
              <Database className="h-4 w-4 text-slate-500" />
              <span className="text-[12px] font-medium text-slate-600">Read from QuickBooks</span>
              <select
                value={readEntity}
                onChange={(e) => setReadEntity(e.target.value)}
                className="px-2 py-1 rounded-md border border-slate-200 text-[12px] bg-white"
              >
                {READ_ENTITIES.map((en) => <option key={en} value={en}>{en}</option>)}
              </select>
              <button
                type="button" onClick={onRead} disabled={reading}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-blue-600 text-white text-[12px] font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {reading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Database className="h-3.5 w-3.5" />}
                Read
              </button>
            </div>
            <p className="mt-1 text-[11px] text-slate-400">
              Read-only preview — nothing is imported into mySMS360. To send
              money records the other way, use the <strong>Write</strong> tab.
            </p>

            {readResult && readResult.count > 0 && (
              <div className="mt-3">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-[12px] text-slate-600 font-medium">
                    {readResult.count} {readResult.entity}{readResult.count === 1 ? '' : 's'}
                  </span>
                  <button type="button" onClick={() => setShowRaw((s) => !s)}
                    className="text-[11px] text-blue-600 hover:underline">
                    {showRaw ? 'Table view' : 'Raw JSON'}
                  </button>
                </div>
                {showRaw ? (
                  <pre className="max-h-80 overflow-auto rounded-md border border-slate-200 bg-white p-2 text-[11px] text-slate-700">
                    {JSON.stringify(readResult.rows.slice(0, 50), null, 2)}
                  </pre>
                ) : (
                  <div className="max-h-80 overflow-auto rounded-md border border-slate-200 bg-white">
                    <table className="w-full text-[12px]">
                      <thead className="bg-slate-50 text-slate-500 sticky top-0">
                        <tr>{columnsFor(readResult.rows).map((c) => (
                          <th key={c} className="text-left font-medium px-2 py-1.5 whitespace-nowrap">{c}</th>
                        ))}</tr>
                      </thead>
                      <tbody>
                        {readResult.rows.slice(0, 50).map((row, i) => (
                          <tr key={row?.Id || i} className="border-t border-slate-100">
                            {columnsFor(readResult.rows).map((c) => (
                              <td key={c} className="px-2 py-1.5 text-slate-700 whitespace-nowrap max-w-[220px] truncate">
                                {cellVal(row?.[c])}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* WRITE — the real accounting sync (mySMS360 -> QuickBooks). Was a
            separate card below the credentials form; nobody scrolled that far. */}
        {qbTab === 'write' && qb?.configured && (
          <QbSyncPanel facilityId={facilityId} timeZone={timeZone} />
        )}
        {qbTab === 'write' && !qb?.configured && (
          <p className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3 text-[12px] text-slate-500">
            Connect QuickBooks below first — then this tab maps your income
            accounts and starts pushing invoices, payments and refunds.
          </p>
        )}

        {/* One-click OAuth connect */}
        <div className="mb-4">
          <button
            type="button" onClick={onConnect} disabled={connecting || !qb?.oauth_ready}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-white text-[13px] font-semibold hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: '#2CA01C' }}
          >
            {connecting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Link2 className="h-4 w-4" />}
            {qb?.status === 'connected' ? 'Reconnect with QuickBooks' : 'Connect with QuickBooks'}
          </button>
          {!qb?.oauth_ready && (
            <p className="mt-1.5 text-[11px] text-slate-400">
              {qb?.platform_app
                ? 'One-click connect isn’t available — ask the platform admin to set the QuickBooks redirect URI.'
                : 'Enter your Client ID & Secret below and Save, then click Connect.'}
            </p>
          )}
        </div>

        <div className="flex items-center gap-2 my-3">
          <div className="h-px flex-1 bg-slate-100" />
          <span className="text-[11px] uppercase tracking-wider text-slate-400">Or enter manually</span>
          <div className="h-px flex-1 bg-slate-100" />
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
          <div>
            <label className="block text-[12px] font-medium text-slate-600 mb-1">Environment</label>
            <select {...register('environment')}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px] bg-white">
              <option value="production">Production (real company data)</option>
              <option value="sandbox">Sandbox (testing)</option>
            </select>
          </div>

          <Field label="Realm ID (Company ID)" error={errors.realm_id}>
            <input {...register('realm_id')} placeholder="e.g. 4620816365…"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]" />
          </Field>

          <Field label="Client ID" error={errors.client_id}>
            <input {...register('client_id')} placeholder={ph(qb?.has_client_id, 'From your Intuit app → Keys & Credentials')}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]" autoComplete="off" />
          </Field>

          <Field label="Client Secret" error={errors.client_secret}>
            <input type="password" {...register('client_secret')} placeholder={ph(qb?.has_client_secret, 'From your Intuit app → Keys & Credentials')}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]" autoComplete="off" />
          </Field>

          <Field label="Refresh Token" error={errors.refresh_token}>
            <input type="password" {...register('refresh_token')} placeholder={ph(qb?.has_refresh_token, 'From the QuickBooks OAuth connect')}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-[13px]" autoComplete="off" />
          </Field>

          <div className="flex items-center gap-2 pt-1">
            <button type="submit" disabled={saving}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" onClick={onTest} disabled={testing || !qb?.configured}
              title={qb?.configured ? 'Verify the connection' : 'Enter all fields and Save first'}
              className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg bg-blue-600 text-[13px] font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {testing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Test connection
            </button>
            {qb?.configured && (
              <button type="button" onClick={onDisconnect}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-lg border border-red-200 text-[13px] font-medium text-red-600 hover:bg-red-50">
                <Link2Off className="h-3.5 w-3.5" /> Disconnect
              </button>
            )}
          </div>
        </form>

        <div className="mt-4 pt-4 border-t border-slate-100 text-[12px] text-slate-400">
          Create an app in the{' '}
          <a href="https://developer.intuit.com/" target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-0.5 text-blue-600 hover:underline">
            Intuit Developer portal <ExternalLink className="h-3 w-3" />
          </a>{' '}
          to get the Client ID/Secret, then authorize your company (
          {env === 'sandbox' ? 'sandbox' : 'production'}) to obtain the Realm ID + Refresh Token.
          Credentials are encrypted before they are stored.
        </div>
      </div>
      )}

      {appTab === 'webhooks' && <WebhooksCard />}
    </div>
  );
};


// ---------------------------------------------------------------------------
// QB TRUE accounting sync — account mapping + status + backfill + match.
// One-way: mySMS360 is the system of record, QBO is the book of record.
// ---------------------------------------------------------------------------
const MAP_FIELDS = [
  { key: 'income_rent', label: 'Rent income account', kind: 'income' },
  { key: 'income_late_fee', label: 'Late fee income account', kind: 'income' },
  { key: 'income_nsf', label: 'NSF fee income account', kind: 'income' },
  { key: 'income_other', label: 'Other charges income account', kind: 'income' },
  { key: 'deposit_bank_account', label: 'Deposit-to bank account', kind: 'bank' },
];

const QbSyncPanel = ({ facilityId, timeZone }) => {
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [map, setMap] = useState({});
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState('');
  const [note, setNote] = useState(null);
  const [backfill, setBackfill] = useState(null);
  const [matches, setMatches] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const res = await integrationsService.qbSyncStatus(facilityId);
      setStatus(res.data);
      setEnabled(!!res.data.enabled);
    } catch { /* card is best-effort */ }
  }, [facilityId]);

  useEffect(() => { refresh(); }, [refresh]);

  const loadAccounts = async () => {
    setBusy('accounts');
    setNote(null);
    try {
      const res = await integrationsService.readQuickBooks(facilityId, 'Account', 200);
      setAccounts(res.data.rows || []);
    } catch (e) {
      setNote({ type: 'err', text: e?.response?.data?.detail || 'Could not load QBO accounts.' });
    } finally { setBusy(''); }
  };

  const optionsFor = (kind) => accounts.filter((a) => {
    const t = `${a.AccountType || ''} ${a.Classification || ''}`.toLowerCase();
    return kind === 'bank' ? t.includes('bank') : t.includes('income') || t.includes('revenue');
  });

  const save = async () => {
    setBusy('save');
    setNote(null);
    try {
      await integrationsService.qbSaveSyncSettings(facilityId, map, enabled);
      setNote({ type: 'ok', text: enabled ? 'Mapping saved — sync is ON (runs hourly).' : 'Mapping saved. Enable when ready.' });
      refresh();
    } catch (e) {
      setNote({ type: 'err', text: e?.response?.data?.detail || 'Save failed.' });
    } finally { setBusy(''); }
  };

  const runBackfill = async (mode) => {
    setBusy(mode === 'preview' ? 'bf-preview' : 'bf-commit');
    setNote(null);
    try {
      const res = await integrationsService.qbBackfill(facilityId, '2000-01-01', mode);
      setBackfill(res.data);
      if (mode === 'commit') {
        setNote({ type: 'ok', text: `${res.data.total} records queued — they sync on the next run.` });
        refresh();
      }
    } catch (e) {
      setNote({ type: 'err', text: e?.response?.data?.detail || 'Backfill failed.' });
    } finally { setBusy(''); }
  };

  const previewMatches = async () => {
    setBusy('match');
    setNote(null);
    try {
      const res = await integrationsService.qbMatchPreview(facilityId);
      setMatches(res.data);
    } catch (e) {
      setNote({ type: 'err', text: e?.response?.data?.detail || 'Match preview failed.' });
    } finally { setBusy(''); }
  };

  const commitMatches = async () => {
    setBusy('match-commit');
    try {
      const res = await integrationsService.qbMatchCommit(facilityId, matches.matches);
      setNote({ type: 'ok', text: `${res.data.stamped} customers linked to existing QuickBooks records.` });
      setMatches(null);
      refresh();
    } catch (e) {
      setNote({ type: 'err', text: e?.response?.data?.detail || 'Match commit failed.' });
    } finally { setBusy(''); }
  };

  const runNow = async () => {
    setBusy('run');
    setNote(null);
    try {
      const res = await integrationsService.qbSyncRun(facilityId);
      setNote({ type: 'ok', text: `Synced ${res.data.drained} records (${res.data.errors} errors).` });
      refresh();
    } catch (e) {
      setNote({ type: 'err', text: e?.response?.data?.detail || 'Sync run failed.' });
    } finally { setBusy(''); }
  };

  return (
    <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50/60 p-3">
      <div className="flex items-start justify-between mb-1">
        <div>
          <h4 className="text-[13px] font-semibold text-slate-700">Accounting sync</h4>
          <p className="text-[12px] text-slate-400">
            Pushes every invoice, payment and refund into QuickBooks continuously —
            hand your CPA the QuickBooks file, no exports.
          </p>
        </div>
        {status?.enabled ? (
          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">ON</span>
        ) : (
          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-slate-100 text-slate-500 border border-slate-200">OFF</span>
        )}
      </div>

      {note && (
        <div className={`my-3 rounded-lg px-3 py-2 text-[12px] border ${
          note.type === 'ok'
            ? 'border-emerald-100 bg-emerald-50 text-emerald-700'
            : 'border-red-100 bg-red-50 text-red-600'}`}>
          {note.text}
        </div>
      )}

      {status && (
        <div className="flex flex-wrap gap-4 text-[12px] text-slate-500 my-3">
          <span>Pending: <span className="font-semibold text-slate-800">{status.pending}</span></span>
          <span>Synced: <span className="font-semibold text-slate-800">{status.done}</span></span>
          {/* Facility clock, not UTC (owner rule 2026-07-29) — a stored
              naive-UTC stamp shown raw reads as the wrong evening. */}
          {status.last_sync_at && (
            <span>Last run: {formatDateTime(status.last_sync_at, timeZone)}</span>
          )}
        </div>
      )}

      {/* Step 1: account mapping */}
      {accounts.length === 0 ? (
        <button onClick={loadAccounts} disabled={busy === 'accounts'}
          className="px-3.5 py-2 rounded-lg bg-blue-600 text-[13px] font-medium text-white hover:bg-blue-700">
          {busy === 'accounts' ? 'Loading accounts…' : (status?.mapping_saved ? 'Edit account mapping' : 'Set up account mapping')}
        </button>
      ) : (
        <div className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {MAP_FIELDS.map((f) => (
              <div key={f.key}>
                <label className="block text-[12px] font-medium text-slate-600 mb-1">{f.label}</label>
                <select
                  value={map[f.key] || ''}
                  onChange={(e) => setMap((m) => ({ ...m, [f.key]: e.target.value }))}
                  className="w-full rounded-lg border border-slate-200 px-3 py-2 text-[13px]"
                >
                  <option value="">— pick a QuickBooks account —</option>
                  {optionsFor(f.kind).map((a) => (
                    <option key={a.Id} value={a.Id}>{a.Name} ({a.AccountType})</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
          <label className="flex items-center gap-2 text-[13px] text-slate-700 select-none">
            <input type="checkbox" checked={enabled}
              onChange={(e) => setEnabled(e.target.checked)}
              className="rounded border-slate-300" />
            Enable continuous sync (hourly)
          </label>
          <button onClick={save} disabled={busy === 'save'}
            className="px-4 py-2 rounded-lg bg-blue-600 text-white text-[13px] font-medium hover:bg-blue-700 disabled:opacity-50">
            {busy === 'save' ? 'Saving…' : 'Save mapping'}
          </button>
        </div>
      )}

      {/* Step 2: migration match + backfill (after mapping saved) */}
      {status?.mapping_saved && (
        <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap gap-2">
          <button onClick={previewMatches} disabled={!!busy}
            className="px-3.5 py-2 rounded-lg bg-blue-600 text-[12px] font-medium text-white hover:bg-blue-700">
            {busy === 'match' ? 'Matching…' : 'Match existing QB customers'}
          </button>
          <button onClick={() => runBackfill('preview')} disabled={!!busy}
            className="px-3.5 py-2 rounded-lg bg-blue-600 text-[12px] font-medium text-white hover:bg-blue-700">
            {busy === 'bf-preview' ? 'Counting…' : 'Preview backfill'}
          </button>
          {backfill?.mode === 'preview' && backfill.total > 0 && (
            <button onClick={() => runBackfill('commit')} disabled={!!busy}
              className="px-3.5 py-2 rounded-lg bg-blue-600 text-white text-[12px] font-semibold hover:bg-blue-700">
              Queue {backfill.total} records
            </button>
          )}
          {status?.enabled && (
            <button onClick={runNow} disabled={!!busy}
              className="px-3.5 py-2 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-[12px] font-semibold hover:bg-emerald-100">
              {busy === 'run' ? 'Syncing…' : 'Run sync now'}
            </button>
          )}
        </div>
      )}

      {backfill?.mode === 'preview' && (
        <p className="mt-2 text-[12px] text-slate-500">
          Unsynced: {backfill.counts.customer} customers, {backfill.counts.invoice} invoices,{' '}
          {backfill.counts.payment} payments, {backfill.counts.refund} refunds.
        </p>
      )}

      {/* Match confirmation table */}
      {matches && (
        <div className="mt-3 rounded-lg border border-slate-200/80 overflow-hidden">
          <div className="px-3 py-2 bg-slate-50 text-[12px] text-slate-600 flex items-center justify-between">
            <span>{matches.matches.length} matches found · {matches.unmatched} will be created new</span>
            {matches.matches.length > 0 && (
              <button onClick={commitMatches} disabled={busy === 'match-commit'}
                className="px-3 py-1 rounded bg-blue-600 text-white text-[11px] font-semibold hover:bg-blue-700">
                {busy === 'match-commit' ? 'Linking…' : 'Confirm & link'}
              </button>
            )}
          </div>
          <div className="max-h-48 overflow-y-auto divide-y divide-slate-50">
            {matches.matches.map((m) => (
              <div key={m.customer_id} className="px-3 py-1.5 text-[12px] text-slate-600 flex justify-between">
                <span>{m.full_name}</span>
                <span className="text-slate-400">= QB “{m.qb_display_name}” ({m.matched_by})</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Errors */}
      {status?.errors?.length > 0 && (
        <div className="mt-3 rounded-lg border border-rose-200/80 bg-rose-50/40 p-3">
          <p className="text-[12px] font-semibold text-rose-700 mb-1.5">
            {status.errors.length} record(s) failed after retries
          </p>
          {status.errors.map((e2) => (
            <div key={e2.id} className="flex items-center justify-between text-[11px] text-rose-700 py-0.5">
              <span className="truncate mr-2">{e2.kind} {e2.ref_id}: {e2.error}</span>
              <button
                onClick={async () => {
                  await integrationsService.qbSyncRetry(facilityId, e2.id);
                  refresh();
                }}
                className="shrink-0 underline hover:no-underline">retry</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const Field = ({ label, error, children }) => (
  <div>
    <label className="block text-[12px] font-medium text-slate-600 mb-1">{label}</label>
    {children}
    {error && <p className="mt-1 text-[11px] text-red-500">{error.message}</p>}
  </div>
);

export default ThirdPartyAppsSection;
